/** Attach a controller to one of a site's gateways — the standalone form the site page links to. */
import { useEffect, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { useAuth } from '../auth';
import { ApiError, type Gateway, type ProfileSummary } from '../api';
import { Banner, Button } from '../ui';

export function NewController(): JSX.Element {
  const { api } = useAuth();
  const { id = '' } = useParams();
  const navigate = useNavigate();
  const [gateways, setGateways] = useState<Gateway[] | null>(null);
  const [gatewayId, setGatewayId] = useState('');
  const [profiles, setProfiles] = useState<ProfileSummary[]>([]);
  const [profileId, setProfileId] = useState('');
  const [displayName, setDisplayName] = useState('');
  const [host, setHost] = useState('');
  const [portalId, setPortalId] = useState('');
  const [model, setModel] = useState('');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  useEffect(() => {
    api
      .listGateways(id)
      .then((r) => {
        setGateways(r.gateways);
        if (r.gateways[0]) setGatewayId(r.gateways[0].id);
      })
      .catch((e) => setErr(e instanceof ApiError ? e.message : 'Could not load gateways.'));
    api
      .listProfiles()
      .then((r) => {
        setProfiles(r.profiles);
        if (r.profiles[0]) setProfileId(r.profiles[0].id);
      })
      .catch(() => setProfiles([]));
  }, [api, id]);

  // A Cerbo GX system is identified by its VRM portal id, not a LAN address.
  const isVictron = profiles.find((p) => p.id === profileId)?.protocol === 'victron-mqtt';

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    setBusy(true);
    setErr(null);
    try {
      await api.createController(gatewayId, {
        profile_id: profileId,
        ...(displayName.trim() ? { display_name: displayName.trim() } : {}),
        ...(isVictron
          ? { victron_portal_id: portalId.trim(), ...(model.trim() ? { model: model.trim() } : {}) }
          : host.trim()
            ? { host: host.trim() }
            : {}),
      });
      navigate(`/sites/${id}`);
    } catch (e) {
      setErr(e instanceof ApiError ? e.message : 'Could not attach the controller.');
    } finally {
      setBusy(false);
    }
  }

  return (
    <>
      <div className="crumbs">
        <Link to="/sites">Sites</Link> <span className="faint">/</span>{' '}
        <Link to={`/sites/${id}`}>site</Link> <span className="faint">/</span> add controller
      </div>
      <div className="page-head">
        <div>
          <h1>Add controller</h1>
          <div className="lead">Attach a generator controller to a gateway at this site.</div>
        </div>
      </div>

      {err && <Banner kind="err">{err}</Banner>}

      <div className="panel panel-pad">
        {gateways && gateways.length === 0 ? (
          <div className="empty">
            <h3>No gateways yet</h3>
            <p>Register a gateway on the site page first, then attach a controller to it.</p>
            <Link className="btn btn-primary" to={`/sites/${id}`}>
              Back to site
            </Link>
          </div>
        ) : (
          <form onSubmit={submit}>
            <div className="field">
              <label htmlFor="gw">Gateway</label>
              <select
                id="gw"
                className="select"
                value={gatewayId}
                onChange={(e) => setGatewayId(e.target.value)}
                required
              >
                {gateways?.map((g) => (
                  <option key={g.id} value={g.id}>
                    {g.serial}
                  </option>
                ))}
              </select>
            </div>
            <div className="field">
              <label htmlFor="profile">Controller profile</label>
              <select
                id="profile"
                className="select"
                value={profileId}
                onChange={(e) => setProfileId(e.target.value)}
              >
                {profiles.map((p) => (
                  <option key={p.id} value={p.id}>
                    {p.name}
                  </option>
                ))}
              </select>
            </div>
            <div className="field">
              <label htmlFor="dn">
                Display name <span className="opt">(optional)</span>
              </label>
              <input
                id="dn"
                className="input"
                placeholder="Genset 1"
                value={displayName}
                onChange={(e) => setDisplayName(e.target.value)}
              />
            </div>
            {isVictron ? (
              <>
                <div className="field">
                  <label htmlFor="portal">Cerbo VRM portal ID</label>
                  <input
                    id="portal"
                    className="input mono"
                    placeholder="c0619ab1d2e3"
                    value={portalId}
                    onChange={(e) => setPortalId(e.target.value)}
                    required
                  />
                </div>
                <div className="field">
                  <label htmlFor="model">
                    Battery <span className="opt">(optional — shown under the SOC)</span>
                  </label>
                  <input
                    id="model"
                    className="input"
                    placeholder="2 × BYD LVL15.4"
                    value={model}
                    onChange={(e) => setModel(e.target.value)}
                  />
                </div>
              </>
            ) : (
              <div className="field">
                <label htmlFor="host">
                  Controller IP{' '}
                  <span className="opt">(optional — Modbus TCP / Config Suite target)</span>
                </label>
                <input
                  id="host"
                  className="input mono"
                  placeholder="192.168.2.10"
                  value={host}
                  onChange={(e) => setHost(e.target.value)}
                />
              </div>
            )}
            <div className="form-actions">
              <button
                type="button"
                className="btn btn-ghost"
                onClick={() => navigate(`/sites/${id}`)}
              >
                Cancel
              </button>
              <Button type="submit" busy={busy} disabled={!gatewayId || !profileId}>
                Attach controller
              </Button>
            </div>
          </form>
        )}
      </div>
    </>
  );
}
